export interface OutboundUrlValidationResult {
    valid: boolean;
    error?: string;
}

const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

const BLOCKED_HOSTNAMES = new Set([
    "localhost",
    "localhost.localdomain",
    "0.0.0.0"
]);

const MAX_URL_LENGTH = 2048;

const INVALID_URL_ERROR = "Please enter a valid URL.";
const INVALID_SCHEME_ERROR = "Only http and https URLs are allowed.";
const BLOCKED_HOST_ERROR = "URLs pointing to private or reserved network addresses are not allowed.";
const CREDENTIALS_NOT_ALLOWED_ERROR = "URLs with embedded credentials are not allowed.";

const HEX_HEXTET_PATTERN = /^[0-9a-f]{1,4}$/i;

const parseOctalIPv4Part = (part: string): number | null => {
    if (!/^0[0-7]+$/.test(part)) {
        return null;
    }
    const value = parseInt(part, 8);
    return value <= 255 ? value : null;
};

const parseDottedDecimalIPv4 = (hostname: string): [number, number, number, number] | null => {
    const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(hostname);
    if (!match) {
        return null;
    }

    const octets = match.slice(1).map((part) => {
        if (/^0[0-7]+$/.test(part) && part.length > 1) {
            return parseOctalIPv4Part(part);
        }
        const value = Number(part);
        return Number.isInteger(value) && value >= 0 && value <= 255 ? value : null;
    });

    // tslint:disable-next-line:ter-arrow-parens
    if (octets.some((octet) => octet === null)) {
        return null;
    }

    return octets as [number, number, number, number];
};

/* tslint:disable:no-bitwise */
const parseIntegerIPv4 = (hostname: string): [number, number, number, number] | null => {
    if (!/^\d+$/.test(hostname)) {
        return null;
    }

    const value = Number(hostname);
    if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffffff) {
        return null;
    }

    return [
        (value >>> 24) & 0xff,
        (value >>> 16) & 0xff,
        (value >>> 8) & 0xff,
        value & 0xff
    ];
};

const parseHexIPv4 = (hostname: string): [number, number, number, number] | null => {
    if (!/^0x[0-9a-f]+$/i.test(hostname)) {
        return null;
    }

    const value = parseInt(hostname, 16);
    if (!Number.isFinite(value) || value < 0 || value > 0xffffffff) {
        return null;
    }

    return [
        (value >>> 24) & 0xff,
        (value >>> 16) & 0xff,
        (value >>> 8) & 0xff,
        value & 0xff
    ];
};

const parseIPv4Octets = (hostname: string): [number, number, number, number] | null =>
    parseDottedDecimalIPv4(hostname) ||
    parseIntegerIPv4(hostname) ||
    parseHexIPv4(hostname);

const isPrivateOrReservedIPv4 = ([a, b]: [number, number, number, number]): boolean => {
    if (a === 0 || a === 10 || a === 127) {
        return true;
    }
    if (a === 100 && b >= 64 && b <= 127) {
        return true;
    }
    if (a === 169 && b === 254) {
        return true;
    }
    if (a === 172 && b >= 16 && b <= 31) {
        return true;
    }
    if (a === 192 && b === 168) {
        return true;
    }
    if (a === 198 && b >= 18 && b <= 19) {
        return true;
    }
    return false;
};

const isValidHextet = (part: string): boolean => HEX_HEXTET_PATTERN.test(part);

const expandIPv6Hextets = (hostname: string): string[] | null => {
    let address = hostname.toLowerCase();
    const zoneIndex = address.indexOf("%");
    if (zoneIndex !== -1) {
        address = address.slice(0, zoneIndex);
    }

    if (address.includes(".")) {
        return null;
    }

    let headParts: string[];
    let tailParts: string[];

    if (address.split("::").length > 2) {
        return null;
    }

    if (address.includes("::")) {
        const [head = "", tail = ""] = address.split("::");
        headParts = head ? head.split(":").filter(Boolean) : [];
        tailParts = tail ? tail.split(":").filter(Boolean) : [];
        const allParts = [...headParts, ...tailParts];
        // tslint:disable-next-line:ter-arrow-parens
        if (allParts.some((part) => !isValidHextet(part))) {
            return null;
        }
        const missing = 8 - headParts.length - tailParts.length;
        if (missing < 0) {
            return null;
        }
        // tslint:disable-next-line:ter-arrow-parens
        return [...headParts, ...Array(missing).fill("0"), ...tailParts].map((part) => part.padStart(4, "0"));
    }

    headParts = address.split(":").filter(Boolean);
    if (headParts.length !== 8) {
        return null;
    }

    // tslint:disable-next-line:ter-arrow-parens
    if (headParts.some((part) => !isValidHextet(part))) {
        return null;
    }

    // tslint:disable-next-line:ter-arrow-parens
    return headParts.map((part) => part.padStart(4, "0"));
};

const isValidIPv6Hostname = (hostname: string): boolean => {
    const normalized = hostname.toLowerCase();
    const embeddedIpv4Match = normalized.match(/:(\d{1,3}(?:\.\d{1,3}){3})$/);
    if (embeddedIpv4Match) {
        return parseDottedDecimalIPv4(embeddedIpv4Match[1]) !== null;
    }

    return expandIPv6Hextets(normalized) !== null;
};

const isPrivateOrReservedIPv6 = (hostname: string): boolean => {
    const normalized = hostname.toLowerCase();

    if (normalized === "::" || normalized === "0:0:0:0:0:0:0:0") {
        return true;
    }
    if (normalized === "::1" || normalized === "0:0:0:0:0:0:0:1") {
        return true;
    }

    const embeddedIpv4Match = normalized.match(/:(\d{1,3}(?:\.\d{1,3}){3})$/);
    if (embeddedIpv4Match) {
        const octets = parseDottedDecimalIPv4(embeddedIpv4Match[1]);
        return octets ? isPrivateOrReservedIPv4(octets) : true;
    }

    const expanded = expandIPv6Hextets(normalized);
    if (!expanded) {
        return false;
    }

    const first = parseInt(expanded[0], 16);

    // fe80::/10
    if ((first & 0xffc0) === 0xfe80) {
        return true;
    }

    // fc00::/7
    if ((first & 0xfe00) === 0xfc00) {
        return true;
    }

    // ::ffff:x.x.x.x encoded in the final 32 bits across hextets 6 and 7
    if (
        // tslint:disable-next-line:ter-arrow-parens
        expanded.slice(0, 5).every((part) => part === "0000") &&
        expanded[5] === "ffff"
    ) {
        const ipv4Num = (parseInt(expanded[6], 16) << 16) | parseInt(expanded[7], 16);
        const octets: [number, number, number, number] = [
            (ipv4Num >>> 24) & 0xff,
            (ipv4Num >>> 16) & 0xff,
            (ipv4Num >>> 8) & 0xff,
            ipv4Num & 0xff
        ];
        return isPrivateOrReservedIPv4(octets);
    }

    return false;
};
/* tslint:enable:no-bitwise */

const isBlockedHostname = (hostname: string): boolean => {
    const normalized = hostname.toLowerCase().replace(/\.$/, "");

    if (BLOCKED_HOSTNAMES.has(normalized)) {
        return true;
    }

    if (normalized.endsWith(".localhost")) {
        return true;
    }

    const ipv4Octets = parseIPv4Octets(normalized);
    if (ipv4Octets) {
        return isPrivateOrReservedIPv4(ipv4Octets);
    }

    // Reject alternate IPv4 encodings (decimal/hex) even when they map to public addresses.
    if (/^0x[0-9a-f]+$/i.test(normalized) || /^\d+$/.test(normalized)) {
        return true;
    }

    if (normalized.includes(":")) {
        return isPrivateOrReservedIPv6(normalized);
    }

    return false;
};

const hasUrlScheme = (value: string): boolean => /^[a-z][a-z0-9+.-]*:/i.test(value);

/** Node and some runtimes wrap IPv6 literals in square brackets on URL.hostname. */
const normalizeParsedHostname = (hostname: string): string =>
    hostname.toLowerCase().replace(/^\[|\]$/g, "");

/**
 * Rejects strings the URL parser accepts as hostnames but that are not real targets,
 * e.g. bare tokens like "not-a-url" after https:// prefixing.
 */
const isPlausibleHostname = (hostname: string): boolean => {
    if (!hostname) {
        return false;
    }
    if (hostname.includes(":")) {
        return isValidIPv6Hostname(hostname);
    }
    if (parseIPv4Octets(hostname) !== null) {
        return true;
    }
    return hostname.includes(".");
};

/**
 * Ensures a value can be parsed as an absolute URL for validation.
 * Bare hostnames (e.g. example.com) are treated as https URLs.
 */
export const normalizeUrlForValidation = (rawUrl: string): string => {
    if (typeof rawUrl !== "string") {
        return "";
    }
    const trimmed = rawUrl.trim();
    if (!trimmed || hasUrlScheme(trimmed)) {
        return trimmed;
    }
    return `https://${trimmed}`;
};

/**
 * Validates user-supplied outbound URLs before they are sent to backend services.
 * DNS resolution is not performed in the browser; hostname-to-private-IP resolution
 * remains a backend responsibility.
 */
export const validateOutboundUrl = (rawUrl: string): OutboundUrlValidationResult => {
    if (typeof rawUrl !== "string") {
        return { valid: false, error: INVALID_URL_ERROR };
    }

    const url = normalizeUrlForValidation(rawUrl);
    if (!url) {
        return { valid: false, error: INVALID_URL_ERROR };
    }

    if (url.length > MAX_URL_LENGTH) {
        return { valid: false, error: INVALID_URL_ERROR };
    }

    let parsedUrl: URL;
    try {
        parsedUrl = new URL(url);
    } catch {
        return { valid: false, error: INVALID_URL_ERROR };
    }

    if (!ALLOWED_PROTOCOLS.has(parsedUrl.protocol)) {
        return { valid: false, error: INVALID_SCHEME_ERROR };
    }

    if (parsedUrl.username || parsedUrl.password) {
        return { valid: false, error: CREDENTIALS_NOT_ALLOWED_ERROR };
    }

    const hostname = normalizeParsedHostname(parsedUrl.hostname);
    if (!hostname || !isPlausibleHostname(hostname)) {
        return { valid: false, error: INVALID_URL_ERROR };
    }

    if (isBlockedHostname(hostname)) {
        return { valid: false, error: BLOCKED_HOST_ERROR };
    }

    return { valid: true };
};

export const isOutboundUrlValid = (url: string): boolean => validateOutboundUrl(url).valid;
